import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { MzicSvgComponent, SpinnerComponent } from '@mzic/mzic-components';
import {
  AuthInteraction,
  AuthInteractionEndedReason,
  SubmitAuthInteractionRequest,
} from '../../../interfaces/collection.interface';
import { AuthInteractionDraftState } from '../../../services/auth-interaction-persistence.service';
import {
  ConnectionAttemptBannerComponent,
  ConnectionAttemptBannerVariant,
} from '../../connection-attempt-banner/connection-attempt-banner.component';
import { ConnectionEntityBrandChipComponent } from '../../connection-entity-brand-chip/connection-entity-brand-chip.component';
import { ScopeSelectionInteractionComponent } from './scope-selection-interaction/scope-selection-interaction.component';
import {
  AuthInteractionFooterAction,
  AuthInteractionRendererState,
  TwoFaCodeInteractionComponent,
  TwoFaCodeRendererState,
} from './two-fa-code-interaction/two-fa-code-interaction.component';

@Component({
  selector: 'lib-auth-interaction-step',
  imports: [
    CommonModule,
    TranslocoModule,
    MzicSvgComponent,
    SpinnerComponent,
    ConnectionAttemptBannerComponent,
    ConnectionEntityBrandChipComponent,
    TwoFaCodeInteractionComponent,
    ScopeSelectionInteractionComponent,
  ],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './auth-interaction-step.component.html',
  styleUrl: './auth-interaction-step.component.scss',
})
export class AuthInteractionStepComponent {
  @Input({ required: true }) authInteraction!: AuthInteraction;
  @Input() entityName = '';
  @Input() entityIconUrl: string | null = null;
  @Input() integrationEnabled = false;
  @Input() integrationEntityName = '';
  @Input() bannerVariant: ConnectionAttemptBannerVariant | null = null;
  @Input() loading = false;
  @Input() lazyLoading = false;
  @Input() resending = false;
  @Input() submitError: string | null = null;
  @Input() initialDraftState: AuthInteractionDraftState | null = null;
  /** Set when the verification already ended; the screen only offers Back. */
  @Input() endedReason: AuthInteractionEndedReason | null = null;

  @Output() back = new EventEmitter<void>();
  @Output() submitRequested = new EventEmitter<SubmitAuthInteractionRequest>();
  @Output() resendRequested = new EventEmitter<void>();
  @Output() pageRequested = new EventEmitter<{ page: number; size: number }>();
  @Output() draftChanged = new EventEmitter<AuthInteractionDraftState>();

  private readonly payload = signal<Record<string, unknown>>({});
  private readonly twoFaFooter = signal<AuthInteractionFooterAction | null>(
    null,
  );
  private readonly twoFaVerifying = signal(false);
  readonly submitDisabled = signal(true);

  onBack(): void {
    if (this.loading) return;
    this.back.emit();
  }

  onSubmit(): void {
    if (this.loading) return;
    this.submitRequested.emit({
      kind: this.authInteraction.kind,
      payload: this.payload(),
    });
  }

  onFooterAction(): void {
    if (this.footerAction === 'RESTART') {
      this.onBack();
    } else {
      this.onSubmit();
    }
  }

  onRendererStateChange(
    state: AuthInteractionRendererState & {
      draftState?: AuthInteractionDraftState;
    },
  ): void {
    this.payload.set(state.payload);
    this.submitDisabled.set(state.submitDisabled);
    if (state.draftState) {
      this.draftChanged.emit(state.draftState);
    }
  }

  onTwoFaStateChange(state: TwoFaCodeRendererState): void {
    this.onRendererStateChange(state);
    this.twoFaFooter.set(state.footer);
    this.twoFaVerifying.set(state.verifying);
  }

  /** Code and push prompts share the two-factor renderer. */
  get isTwoFactor(): boolean {
    return (
      this.authInteraction.kind === 'TWO_FA_CODE' ||
      this.authInteraction.kind === 'PUSH_APPROVAL'
    );
  }

  /** Spinner in the footer button; 2FA stays busy until the result arrives. */
  get footerBusy(): boolean {
    return this.isTwoFactor ? this.twoFaVerifying() : this.loading;
  }

  /** `null` hides the footer (digit boxes and push submit on their own). */
  get footerAction(): AuthInteractionFooterAction | null {
    if (this.isTwoFactor) return this.twoFaFooter();
    return this.authInteraction.kind === 'SCOPE_SELECTION' ? 'SUBMIT' : 'RESTART';
  }

  get footerLabelKey(): string {
    if (this.footerAction === 'RESTART') {
      return 'lang.auth_interaction_reconnect';
    }
    return this.isTwoFactor && this.twoFaVerifying()
      ? 'lang.auth_interaction_verifying'
      : 'lang.auth_interaction_submit';
  }

  get scopeSelectionTitleKey(): string {
    return 'lang.auth_interaction_scope_title';
  }

  get scopeSelectionSubtitleKey(): string {
    return `lang.auth_interaction_scope_subtitle${this.integrationSuffix}`;
  }

  get integrationSuffix(): string {
    return this.integrationEnabled ? '_integration' : '';
  }
}
